<!-- 
Panel displaying where data is being sourced (from an automatic data source or from a manually uploaded spreadsheet).

Also allows downloading of a populated spreadsheet template for those data sources that will be coming from an uploaded spreadsheet.
-->
<template>
  <div class="my-0">
    <our-loading-indicator v-if="state.loading" />
    <template v-else>
      <our-data-container>
        <our-data-field label="Data source for in-scope providers">
          {{ inScopeText }}
          <div v-if="configuration.useSpreadsheetForInScopeProviders">
            <v-btn small class="mr-2 pl-3 ml-0 default" @click="getTemplate('in-scope', false)">
              <img src="@/assets/excel-logo.svg" alt="Excel document" height="18" class="mr-2">
              Download spreadsheet template
            </v-btn>
            <p class="caption">
              The spreadsheet template is a spreadsheet in the required format, populated with any existing data.
            </p>
          </div>
        </our-data-field>
        <our-data-field label="Data source for levy parameters">
          {{ levyParametersText }}
          <div v-if="configuration.useSpreadsheetForLevyParameters">
            <v-btn small class="mr-2 pl-3 ml-0 default" @click="getTemplate('basic-levy-parameters', levyParametersUsePreviousYearsData)">
              <img src="@/assets/excel-logo.svg" alt="Excel document" height="18" class="mr-2">
              Download spreadsheet template
            </v-btn>
            <p class="caption">
              The spreadsheet template is a spreadsheet in the required format, populated with any existing data.
            </p>
            <v-checkbox v-if="configuration.numberOfLtpsLatePaymentYears > 0" v-model="levyParametersUsePreviousYearsData" color="primary">
              <template v-slot:label>
                <span class="font-weight-regular">Include data from previous years</span>
              </template>
            </v-checkbox>
          </div>
        </our-data-field>
        <our-data-field label="Data source for offline parameters">
          Upload from spreadsheet
          <div>
            <v-btn small class="mr-2 pl-3 ml-0 default" @click="getTemplate('offline-levy-parameters', offlineParametersUsePreviousYearsData)">
              <img src="@/assets/excel-logo.svg" alt="Excel document" height="18" class="mr-2">
              Download spreadsheet template
            </v-btn>
            <p class="caption">
              The spreadsheet template is a spreadsheet in the required format, populated with any existing data.
            </p>
            <v-checkbox v-if="configuration.numberOfAnnualChargeLatePaymentYears > 0" v-model="offlineParametersUsePreviousYearsData" color="primary">
              <template v-slot:label>
                <span class="font-weight-regular">Include data from previous years</span>
              </template>
            </v-checkbox>
          </div>
        </our-data-field>
      </our-data-container>
      <div class="text-xs-center py-2">
        <v-btn v-if="canEdit" @click="showEditDialog=true">
          <v-icon class="mr-2">edit</v-icon>
          Edit
        </v-btn>
      </div>
    </template>
    <data-sources-configuration-dialog v-model="showEditDialog" :levy-collection-id="levyCollectionId" />
  </div>
</template>

<script>
import DataSourcesConfigurationDialog from "@/components/levy-collection-setup/DataSourcesConfigurationDialog.vue";
import * as action from "@/store/levy-collections/action-types";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import * as claim from "@/store/claim-types";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  components: { DataSourcesConfigurationDialog },
  mixins: [lookupMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      showEditDialog: false,
      levyParametersUsePreviousYearsData: false,
      offlineParametersUsePreviousYearsData: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.viewDataSourceConfiguration;
    },
    loaded() {
      return this.state.loaded;
    },
    configuration() {
      return this.state.data;
    },
    canEdit() {
      return this.state.loaded && this.hasPermission && this.doesLevyCollectionStatusPermitEditing;
    },
    hasPermission() {
      return this.$store.getters.claims.includes(claim.LEVY_COLLECTION_CONFIGURE_DATA_SOURCES);
    },
    doesLevyCollectionStatusPermitEditing() {
      return this.doesLookupHaveAttribute(
        this.configuration.status,
        "CanEditDataSourcesConfiguration",
        this.lookups.levyCollectionStatuses
      );
    },
    inScopeText() {
      const ds = this.configuration.type == "Vsl" ? "Import from HITS & VSL DataMart" : "Import from HITS";
      return this.configuration.useSpreadsheetForInScopeProviders ? "Upload from spreadsheet" : ds;
    },
    levyParametersText() {
      const ds = this.configuration.type == "Vsl" ? "Import from VSL DataMart" : "Import from Unistats";
      return this.configuration.useSpreadsheetForLevyParameters ? "Upload from spreadsheet" : ds;
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      const needsReloaded =
        (!this.state.loaded && !this.state.loading) || this.state.levyCollectionId != this.levyCollectionId;
      if (needsReloaded) {
        await this.$store.dispatch(action.VIEW_DATA_SOURCE_CONFIGURATION, this.levyCollectionId);
      }
    },
    async getTemplate(endpointSuffix, usePrevious) {
      await this.$store.dispatch(lcpAction.UPLOAD_GET_TEMPLATE, {
        levyCollectionId: this.levyCollectionId,
        endpointSuffix: endpointSuffix,
        usePrevious: usePrevious
      });
    }
  }
};
</script>
